/**
 * Sparkline path maths, kept separate from the component so it can be tested in
 * the node project without a browser.
 *
 * This consolidates six divergent implementations that disagreed on
 * normalisation, flat-series handling and stroke inset. `normalize` makes the
 * first of those an explicit caller decision rather than an accident.
 */

export type SparklineNormalize = 'minMax' | 'zeroMax';

export interface SparklineGeometryOptions {
  width: number;
  height: number;
  strokeWidth: number;
  normalize: SparklineNormalize;
}

export interface SparklineGeometry {
  /** SVG path `d` for the line. Empty string when there is no data. */
  line: string;
  /** SVG path `d` for the filled area, closed to the baseline. */
  area: string;
  /** Last plotted point, for the optional end marker. */
  endPoint: { x: number; y: number } | null;
}

const round = (n: number): number => Math.round(n * 100) / 100;

export function buildSparklinePath(
  values: readonly number[],
  { width, height, strokeWidth, normalize }: SparklineGeometryOptions,
): SparklineGeometry {
  if (values.length === 0) {
    return { line: '', area: '', endPoint: null };
  }

  const inset = strokeWidth;
  const usableHeight = Math.max(0, height - inset * 2);
  const max = Math.max(...values);
  const min = normalize === 'zeroMax' ? 0 : Math.min(...values);
  const span = max - min;

  const stepX = values.length > 1 ? width / (values.length - 1) : 0;

  const points = values.map((value, index) => {
    const x = index * stepX;
    // A flat series has no meaningful position within its own range, so it sits
    // mid-height rather than collapsing onto the baseline.
    const ratio = span === 0 ? 0.5 : (value - min) / span;
    const y = inset + (1 - ratio) * usableHeight;
    return { x, y };
  });

  const line = points
    .map((point, index) => `${index === 0 ? 'M' : 'L'}${round(point.x)},${round(point.y)}`)
    .join(' ');

  const baseline = height - inset;
  const first = points[0];
  const last = points[points.length - 1];
  const area = `${line} L${round(last.x)},${round(baseline)} L${round(first.x)},${round(baseline)} Z`;

  return { line, area, endPoint: { x: last.x, y: last.y } };
}
